import React, { useState, useEffect, useRef, useCallback } from 'react';
import InteractiveIssueView from './components/InteractiveContent/InteractiveIssueView';

// Timing constants for smooth UI transitions (in milliseconds)
const MESSAGE_DELAY_MS = 500;
const INITIAL_MESSAGE_DELAY_MS = 100;
const BACK_NAVIGATION_DELAY_MS = 100;

// Validate tree structure
const validateTree = (tree, name) => {
  if (!tree || typeof tree !== 'object') {
    console.error(`Invalid tree structure for ${name}: not an object`);
    return false;
  }
  if (!tree.question || typeof tree.question !== 'string') {
    console.error(`Invalid tree structure for ${name}: missing or invalid question`);
    return false;
  }
  if (!tree.options || !Array.isArray(tree.options) || tree.options.length === 0) {
    console.error(`Invalid tree structure for ${name}: missing or invalid options array`);
    return false;
  }
  return true;
};

// Fetch trees from public directory
const fetchTrees = async () => {
  try {
    const [personalResponse, workplaceResponse] = await Promise.all([
      fetch('/data/trees/personal.json'),
      fetch('/data/trees/workplace.json')
    ]);
    
    if (!personalResponse.ok || !workplaceResponse.ok) {
      throw new Error('Failed to load trees');
    }
    
    const [personalTree, workplaceTree] = await Promise.all([
      personalResponse.json(),
      workplaceResponse.json()
    ]);
    
    return { personalTree, workplaceTree };
  } catch (error) {
    console.error('Error fetching trees:', error);
    throw error;
  }
};

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center h-screen bg-gradient-to-br from-indigo-50 to-purple-50">
          <div className="max-w-md bg-white rounded-lg p-8 shadow-lg border border-red-200">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Something went wrong</h2>
            <p className="text-gray-600 mb-4">
              We encountered an unexpected error. Please try refreshing the page.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Loading Spinner Component
const LoadingSpinner = () => (
  <div className="flex items-center justify-center p-4">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
    <span className="ml-3 text-gray-600">Loading...</span>
  </div>
);

const RealityDecoderChatbot = () => {
  const [taxonomy, setTaxonomy] = useState('personal');
  const [messages, setMessages] = useState([]);
  const [currentNode, setCurrentNode] = useState(null);
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showInteractiveContent, setShowInteractiveContent] = useState(false);
  const [currentIssue, setCurrentIssue] = useState(null);
  const [currentIssueName, setCurrentIssueName] = useState(null);
  const [trees, setTrees] = useState({ personal: null, workplace: null });
  const [treesError, setTreesError] = useState(null);
  const initialized = useRef(false);
  const messagesEndRef = useRef(null);

  // Fetch trees from public directory on mount
  useEffect(() => {
    const loadTrees = async () => {
      try {
        const { personalTree, workplaceTree } = await fetchTrees();
        
        // Validate trees before using them
        if (!validateTree(personalTree, 'personal') || !validateTree(workplaceTree, 'workplace')) {
          setTreesError('Invalid tree structure');
          setIsLoading(false);
          return;
        }
        
        setTrees({ personal: personalTree, workplace: workplaceTree });
      } catch (error) {
        console.error('Failed to load trees:', error);
        setTreesError('Failed to load decision trees');
        setIsLoading(false);
      }
    };
    
    loadTrees();
  }, []);

  const currentTree = trees[taxonomy];

  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current && typeof messagesEndRef.current.scrollIntoView === 'function') {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const addBotMessage = useCallback((text, options = null) => {
    setMessages(prev => [...prev, { type: 'bot', text, options }]);
  }, []);

  const startConversation = useCallback((tree, greeting) => {
    // Validate tree before using it
    if (!validateTree(tree, 'conversation')) {
      setIsLoading(false);
      return;
    }

    setMessages([]);
    setHistory([]);
    setCurrentNode(tree);
    setIsLoading(true);
    
    setTimeout(() => {
      addBotMessage(
        greeting + " Let's start: " + tree.question,
        tree.options.map(opt => opt.text)
      );
      setIsLoading(false);
    }, INITIAL_MESSAGE_DELAY_MS);
  }, [addBotMessage]);

  // Start conversation when trees are loaded
  useEffect(() => {
    if (!trees.personal || !trees.workplace) {
      return;
    }
    
    if (initialized.current) {
      return;
    }
    
    initialized.current = true;
    const greeting = taxonomy === 'personal'
      ? "Hello! I'm the Reality Decoder. I'll help you identify what you're experiencing by asking a few questions."
      : "Hello! I'll help you identify workplace challenges you're facing by asking a few questions.";
    startConversation(trees[taxonomy], greeting);
  }, [trees, taxonomy, startConversation]);

  // Handle taxonomy change - resets to top of new tree
  const handleTaxonomyChange = (newTaxonomy) => {
    if (newTaxonomy === taxonomy || !trees[newTaxonomy]) return;
    setTaxonomy(newTaxonomy);
    const greeting = newTaxonomy === 'personal'
      ? "Hello! I'm the Reality Decoder. I'll help you identify what you're experiencing by asking a few questions."
      : "Hello! I'll help you identify workplace challenges you're facing by asking a few questions.";
    startConversation(trees[newTaxonomy], greeting);
  };

  const addUserMessage = (text) => {
    setMessages(prev => [...prev, { type: 'user', text }]);
  };

  const handleOptionClick = (optionIndex) => {
    // Validate currentNode before accessing
    if (!currentNode || !currentNode.options || !currentNode.options[optionIndex]) {
      console.error('Invalid currentNode or option index');
      return;
    }

    const selectedOption = currentNode.options[optionIndex];

    addUserMessage(selectedOption.text);

    // Save current state to history before moving forward
    setHistory(prev => [...prev, { node: currentNode, messagesLength: messages.length }]);

    if (selectedOption.issue_id) {
      // Terminal node - show result and offer to explore interactive content
      setTimeout(() => {
        // Store the current issue info for when user chooses to explore
        setCurrentIssue(selectedOption.issue_id);
        setCurrentIssueName(selectedOption.issue_name);
        addBotMessage(
          `I've identified your experience as:\n\n${selectedOption.issue_name}\n\n${selectedOption.text}\n\nIssue ID: ${selectedOption.issue_id}\n\nWould you like to explore this issue in depth?`,
          ['Yes, explore this issue', 'No, thank you', '← Go back']
        );
        setCurrentNode({ isFinal: true });
      }, MESSAGE_DELAY_MS);
    } else if (selectedOption.options) {
      // Navigate to sub-options (option itself is the next node)
      setTimeout(() => {
        addBotMessage(selectedOption.question, selectedOption.options.map(opt => opt.text));
        setCurrentNode(selectedOption);
      }, MESSAGE_DELAY_MS);
    }
  };

  const handleFinalChoice = (optionIndex) => {
    if (optionIndex === 0) {
      // Explore this issue - switch to interactive content
      if (currentIssue && currentIssueName) {
        addUserMessage("Yes, explore this issue");
        setTimeout(() => {
          setShowInteractiveContent(true);
        }, MESSAGE_DELAY_MS);
      } else {
        // Fallback if no issue info
        startConversation(currentTree, "Let's explore another experience.");
      }
    } else if (optionIndex === 1) {
      // No thank you
      addUserMessage("No, thank you");
      setTimeout(() => {
        addBotMessage("Thank you for using Reality Decoder. I hope this helps you understand your experience better. Take care!");
      }, MESSAGE_DELAY_MS);
    } else if (optionIndex === 2) {
      // Go back from final result
      if (history.length > 0) {
        addUserMessage("← Go back");

        const previousState = history[history.length - 1];

        setTimeout(() => {
          setMessages(prev => prev.slice(0, -3));
          setCurrentNode(previousState.node);
          setHistory(prev => prev.slice(0, -1));
        }, BACK_NAVIGATION_DELAY_MS);
      }
    }
  };

  // Handle back from interactive content
  const handleBackFromInteractive = () => {
    setShowInteractiveContent(false);
    setCurrentIssue(null);
    setCurrentIssueName(null);
    // Show completion message in chatbot
    setTimeout(() => {
      addBotMessage(
        "Great work exploring that issue! You've completed the interactive content. Would you like to explore another issue or start over?",
        ['Yes, start over', 'No, thank you']
      );
      setCurrentNode({ isFinal: true });
    }, MESSAGE_DELAY_MS);
  };

  // Handle interactive content completion
  const handleInteractiveComplete = () => {
    setShowInteractiveContent(false);
    setCurrentIssue(null);
    setCurrentIssueName(null);
    setTimeout(() => {
      addBotMessage(
        "Excellent! You've completed working through this issue. The insights and reflections you've captured are saved. Would you like to explore another issue?",
        ['Yes, start over', 'No, thank you']
      );
      setCurrentNode({ isFinal: true });
    }, MESSAGE_DELAY_MS);
  };

  const handleGoBack = () => {
    if (history.length === 0) return;

    const previousState = history[history.length - 1];

    setMessages(prev => prev.slice(0, -2));
    setCurrentNode(previousState.node);
    setHistory(prev => prev.slice(0, -1));
  };

  const handleKeyDown = (event, callback) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      callback();
    }
  };

  // Render either chatbot or interactive content
  if (showInteractiveContent && currentIssue) {
    return (
      <div className="flex flex-col h-screen bg-gradient-to-br from-indigo-50 to-purple-50">
        <div className="bg-white shadow-sm border-b border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-indigo-600">Reality Decoder</h1>
              <p className="text-sm text-gray-600">Interactive Exploration</p>
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          <div className="max-w-4xl mx-auto">
            <InteractiveIssueView
              issueId={currentIssue}
              issueName={currentIssueName}
              onBack={handleBackFromInteractive}
              onComplete={handleInteractiveComplete}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gradient-to-br from-indigo-50 to-purple-50">
      <div className="bg-white shadow-sm border-b border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-indigo-600">Reality Decoder</h1>
            <p className="text-sm text-gray-600">Issue Classifier</p>
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="taxonomy-select" className="text-sm text-gray-600">Mode:</label>
            <select
              id="taxonomy-select"
              value={taxonomy}
              onChange={(e) => handleTaxonomyChange(e.target.value)}
              disabled={!trees.personal || !trees.workplace}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white hover:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="personal">Personal Development</option>
              <option value="workplace">Workplace</option>
            </select>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {treesError ? (
          <div className="flex items-center justify-center h-full">
            <div className="max-w-md bg-white rounded-lg p-8 shadow-lg border border-red-200">
              <h2 className="text-xl font-bold text-red-600 mb-4">Error Loading Data</h2>
              <p className="text-gray-600 mb-4">{treesError}</p>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        ) : isLoading && messages.length === 0 && (
          <LoadingSpinner />
        )}
        
        {messages.map((msg, idx) => (
          <div key={idx} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-2xl ${msg.type === 'user' ? 'bg-indigo-600 text-white' : 'bg-white border border-gray-200'} rounded-lg p-4 shadow-sm`}>
              <div className="whitespace-pre-wrap">{msg.text}</div>
              {msg.options && idx === messages.length - 1 && (
                <div className="mt-4 space-y-2">
                  {msg.options.map((option, optIdx) => (
                    <button
                      key={optIdx}
                      onClick={() => currentNode?.isFinal ? handleFinalChoice(optIdx) : handleOptionClick(optIdx)}
                      onKeyDown={(e) => handleKeyDown(e, () => currentNode?.isFinal ? handleFinalChoice(optIdx) : handleOptionClick(optIdx))}
                      className="w-full text-left p-3 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors border border-indigo-200 text-indigo-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      aria-label={`Option ${optIdx + 1}: ${option}`}
                      tabIndex={0}
                    >
                      {optIdx + 1}. {option}
                    </button>
                  ))}
                  {currentNode && !currentNode.isFinal && currentNode !== currentTree && history.length > 0 && (
                    <button
                      onClick={handleGoBack}
                      onKeyDown={(e) => handleKeyDown(e, handleGoBack)}
                      className="w-full text-left p-3 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors border border-gray-300 text-gray-700 font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      aria-label="Go back to previous question"
                      tabIndex={0}
                    >
                      ← Go back
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
};

// Wrap with Error Boundary
const App = () => (
  <ErrorBoundary>
    <RealityDecoderChatbot />
  </ErrorBoundary>
);

export default App;
